import { Link } from "components";
import {
  normolizeDecimalsForInputs,
  normolizeDecimalsWithNoCode,
} from "helpers/funcs";
import type { Data, HeadCell } from "components/Table/types";
import type {
  UsersCard,
  RecentCardTransactionInterface,
} from "api/types/cardProduct";
import type {
  CurrencyInterface,
  PhazeProductInterface,
  CardTransactionInterface,
  CreditcoCardsPurchaseHistory,
} from "helpers/types";

import crypto1 from "assets/images/cryptoCards/crypto1.jpg";
import crypto2 from "assets/images/cryptoCards/crypto2.jpg";
import crypto3 from "assets/images/cryptoCards/crypto3.jpg";
import crypto4 from "assets/images/cryptoCards/crypto4.jpg";
import routesPaths from "routes/routesPaths";

export const cryptoCards: PhazeProductInterface[] = [
  {
    brandName: "Standart VISA Card",
    countryName: "",
    currency: "USDT",
    denominations: [10],
    exchangeRate: { test: 0 },
    valueRestrictions: {
      valueRestrictions: 0,
      maxVal: 0,
      minVal: 0,
    },
    productId: 1,
    productImage: crypto1,
    example: "",
    productDescription: "test",
    termsAndConditions: "test",
    howToUse: "test",
    expiryAndValidity: "",
    discount: 0,
  },
  {
    brandName: "Virtual VISA Card",
    countryName: "",
    currency: "USDT",
    denominations: [10],
    exchangeRate: { test: 0 },
    valueRestrictions: {
      valueRestrictions: 0,
      maxVal: 0,
      minVal: 0,
    },
    productId: 2,
    productImage: crypto2,
    example: "",
    productDescription: "test",
    termsAndConditions: "test",
    howToUse: "test",
    expiryAndValidity: "",
    discount: 0,
  },
  {
    brandName: "Standart Mastercard Card",
    countryName: "",
    currency: "USDT",
    denominations: [10],
    exchangeRate: { test: 0 },
    valueRestrictions: {
      valueRestrictions: 0,
      maxVal: 0,
      minVal: 0,
    },
    productId: 3,
    productImage: crypto3,
    example: "",
    productDescription: "test",
    termsAndConditions: "test",
    howToUse: "test",
    expiryAndValidity: "",
    discount: 0,
  },
  {
    brandName: "Virtual Mastercard Card",
    countryName: "",
    currency: "USDT",
    denominations: [10],
    exchangeRate: { test: 0 },
    valueRestrictions: {
      valueRestrictions: 0,
      maxVal: 0,
      minVal: 0,
    },
    productId: 4,
    productImage: crypto4,
    example: "",
    productDescription: "test",
    termsAndConditions: "test",
    howToUse: "test",
    expiryAndValidity: "",
    discount: 0,
  },
];

// export interface CryptoProductInterface extends ExistPhazeProductInterface {
//   cardNumber: string;
//   cardValidityDate: string;
//   cardPin: string;
//   securityCode: string;
// }

export const staticCryptoOverview: string =
  "<p><strong>What is a Crypto Card:</strong><br />A Crypto Card is a financial tool that leverages funds from your cryptocurrency wallet, seamlessly converting them into the desired currency at the prevailing exchange rate. This innovative solution bridges the gap between traditional finance and the burgeoning world of digital assets.</p>\n<p><strong>Functionality:</strong><br />Much like a conventional debit or credit card, a Crypto Card offers versatile functionality, enabling users to make purchases online, in brick - and - mortar stores, and withdraw cash from ATMs.This broad range of usability enhances convenience and accessibility for users across diverse spending scenarios.</p >\n<p><strong>No Credit Check:</strong><br />One of the key advantages of Crypto Cards is their inclusive nature. Unlike traditional credit cards, which often necessitate rigorous credit checks, Crypto Cards typically bypass this requirement. This means that individuals with varying financial backgrounds can readily access and benefit from the features of a Crypto Card.</p >\n<p><strong>Reloadable Feature:</strong><br />With a Crypto Card, there's no need to manually reload funds onto the card. Instead, users enjoy the convenience of a reloadable feature that automatically replenishes funds from their linked cryptocurrency wallet. This streamlined process empowers users with greater control over their spending while eliminating the hassle of manual reloading.</p >\n<p><strong>Security Measures:</strong><br />Ensuring the security of users' funds and transactions is paramount in the realm of Crypto Cards. To this end, Crypto Cards often incorporate robust security measures such as PIN protection and the ability to lock or unlock the card via dedicated mobile applications. These safeguards provide users with peace of mind and safeguard against unauthorized access or fraudulent activity.</p >\n<p><strong>Global Acceptance:</strong><br />Another notable feature of many Crypto Cards is their widespread acceptance on a global scale. This means that users can seamlessly conduct transactions in various currencies and across international borders. Whether traveling abroad or making cross-border purchases, users can rely on the global acceptance of Crypto Cards to facilitate smooth and efficient transactions.</p>\n";

export const staticCryptoActivate: string[] = [
  "Once you've applied for a Crypto Cards, it will be delivered to your registered address.",
  "Carefully read any accompanying materials, including instructions and terms of use.",
  "Most cards require activation through a designated website or a toll-free activation phone number provided with the card.",
  "You'll likely need to provide information such as the card number, security code, and personal details during the activation process.",
  "Set up a Personal Identification Number (PIN) for added security when using the card for transactions or ATM withdrawals.",
  "After completing the activation process, you may receive a confirmation message or email. Ensure the activation was successful before using the card.",
];

export const cryptoTransactionsHeadCells: readonly HeadCell[] = [
  // {
  //   id: "id",
  //   label: "ID",
  // },
  {
    id: "date",
    label: "DATE",
  },
  {
    id: "details",
    label: "DETAILS",
  },
  {
    id: "transactionAmount",
    align: "right",
    label: "TRANSACTION_AMOUNT",
  },
];

export const allCryptoTransactionsHeadCells: readonly HeadCell[] = [
  {
    id: "card",
    label: "CARD",
  },
  {
    id: "date",
    label: "DATE",
  },
  {
    id: "details",
    label: "DETAILS",
  },
  {
    id: "transactionAmount",
    align: "right",
    label: "TRANSACTION_AMOUNT",
  },
];

export const getAllTransactionsData: (
  transaction: RecentCardTransactionInterface,
  userCryptoCards: UsersCard[] | null,
  currencies: CurrencyInterface[] | null,
  t: (value: string) => string
) => Data = (transaction, userCryptoCards, currencies, t) => {
  const tableData: Data = {};

  const userCard = userCryptoCards?.find(
    ({ card: { id } }) => transaction.cardId === id
  );
  const cardName = userCard
    ? `${t(userCard.card.cardType)} ${
        userCard.cardProductTemplate.label
      } card **${userCard.card.maskedCardNumber.slice(12)}`
    : `Card #${transaction.cardId}`;

  tableData.cardId = {
    value: transaction.cardId,
    type: "component",
    component: (
      <Link to={`${routesPaths.usersCryptoCard}${transaction.cardId}`}>
        {cardName}
      </Link>
    ),
    align: "left",
  };

  tableData.date = {
    value: new Date(transaction.createdAt),
    type: "date",
    align: "left",
  };

  tableData.details = {
    value: transaction.merchantName,
    align: "left",
  };

  tableData.transactionAmount = {
    value: `${normolizeDecimalsWithNoCode(
      transaction.amountInTransactionCurrency,
      transaction.transactionCurrency,
      currencies
    )} ${
      transaction.transactionCurrency !== "USD" &&
      transaction.transactionCurrency !== "EUR"
        ? transaction.transactionCurrency
        : ""
    }`,
    subText:
      transaction.cardCurrency === transaction.transactionCurrency
        ? ""
        : `(${normolizeDecimalsWithNoCode(
            transaction.amountInCardCurrency,
            transaction.cardCurrency,
            currencies
          ).replace("-", "")})`,
  };

  return tableData;
};

export const getTransactionsData: (
  transaction: CardTransactionInterface,
  currencies: CurrencyInterface[] | null
) => Data = (transaction, currencies) => {
  const tableData: Data = {};

  // tableData.id = {
  //   value: transaction.id,
  //   visibleValue: String(transaction.id).slice(0, 6).toUpperCase(),
  //   align: "left",
  //   type: "withCopy",
  // };

  tableData.date = {
    value: new Date(transaction.createdAt),
    type: "date",
    align: "left",
  };

  tableData.details = {
    value: transaction.merchantName,
    align: "left",
  };

  tableData.transactionAmount = {
    value: `${normolizeDecimalsWithNoCode(
      transaction.amountInTransactionCurrency,
      transaction.transactionCurrency,
      currencies
    )}${
      transaction.transactionCurrency !== "USD" &&
      transaction.transactionCurrency !== "EUR"
        ? ` ${transaction.transactionCurrency}`
        : ""
    }`,
    subText:
      transaction.cardCurrency === transaction.transactionCurrency
        ? ""
        : `(${normolizeDecimalsWithNoCode(
            transaction.amountInCardCurrency,
            transaction.cardCurrency,
            currencies
          ).replace("-", "")})`,
  };

  return tableData;
};

export const creditcoPurchaseHistoryHeadCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "ORDER_ID",
  },
  {
    id: "executed",
    label: "EXECUTED",
  },
  {
    id: "type",
    label: "TYPE",
  },
  {
    id: "amount",
    label: "AMOUNT",
    align: "right",
  },
  {
    id: "balance",
    align: "right",
    label: "BALANCE",
  },
];

export const getCreditcoPurchaseHistoryData: (
  item: CreditcoCardsPurchaseHistory,
  currencies: CurrencyInterface[] | null
) => Data = (item, currencies) => {
  const tableData: Data = {};

  tableData.id = {
    value: item.uuid,
    visibleValue: String(item.uuid).slice(0, 6).toUpperCase(),
    align: "left",
    type: "withCopy",
  };

  tableData.executed = {
    value: new Date(item.createdAt),
    type: "date",
    align: "left",
  };

  tableData.type = {
    value: item.type,
    align: "left",
  };

  tableData.amount = {
    value: normolizeDecimalsForInputs(
      item.amount,
      item.currencyCode,
      currencies
    ),
    subText: item.currencyCode,
  };

  tableData.balance = {
    value: normolizeDecimalsForInputs(item.balance, "EUR", currencies),
    subText: "EUR",
  };

  return tableData;
};

interface MCCDataInterface {
  code: string;
  description: string;
}
export const mccDataFirst: MCCDataInterface[] = [
  { code: "3000-3301", description: "MCC_MAIN_AIRLINES" },
  { code: "4511", description: "MCC_AIRLINES" },
  { code: "4722", description: "MCC_TRAVEL_AGENCIES" },
  { code: "4789", description: "MCC_TRANSPORT_MISC" },
  { code: "4816", description: "MCC_NETWORK_SERVICES" },
  { code: "4899", description: "MCC_PAY_TV" },
  { code: "5045", description: "MCC_COMPUTERS_SOFTWARE" },
  { code: "5734", description: "MCC_SOFTWARE_STORES" },
  { code: "5815", description: "MCC_DIGITAL_MEDIA" },
  { code: "5816", description: "MCC_DIGITAL_APPS" },
];

export const mccDataSecond: MCCDataInterface[] = [
  { code: "5817", description: "MCC_DIGITAL_GAMES" },
  { code: "5818", description: "MCC_DIGITAL_LARGE" },
  { code: "5968", description: "MCC_DIRECT_CONTINUITY" },
  { code: "5969", description: "MCC_DIRECT_OTHER" },
  { code: "5999", description: "MCC_RETAIL_MISC" },
  { code: "6051", description: "MCC_FOREX" },
  { code: "7011", description: "MCC_HOTELS" },
  { code: "7311", description: "MCC_ADVERTISING" },
  { code: "7399", description: "MCC_BUSINESS_OTHER" },
  { code: "8999", description: "MCC_PROFESSIONAL_OTHER" },
];
